'use client';

import React from 'react';
import NavBar from '@/components/header/NavBar';
import Footer from '@/components/Footer/Footer';
import { Link } from '@/i18n/routing';

const PrivacyPolicy: React.FC = () => {
  return (
    <>
      <NavBar />

      {/* Hero */}
      <div
        className="bg-cover bg-center bg-no-repeat h-96 lg:h-[500px] relative"
        style={{ backgroundImage: `url('/hero/privacy.avif')` }}
      >
        <div className="absolute inset-0 bg-secondary/55 h-96 lg:h-[500px]" />
        <h1 className="absolute inset-0 flex items-center pt-20 lg:pt-40 justify-center text-white text-5xl lg:text-9xl font-extrabold text-center px-4">
          Privacy Policy
        </h1>
      </div>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-4 lg:px-0 py-16 lg:py-24 text-gray-700 leading-relaxed">
        <p className="mb-6 text-sm text-gray-500">Last updated: September 23, 2026</p>

        <p className="mb-6">
          Evinov (&quot;Evinov&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) is committed to
          protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and
          safeguard your information when you visit our website or use our services. Please read
          this policy carefully. By using our site, you agree to the practices described here.
        </p>

        <h2 className="text-2xl font-bold text-secondary mt-10 mb-4">1. Information We Collect</h2>
        <p className="mb-4">We may collect the following types of information:</p>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li><strong>Personal Information</strong> — such as your name, email address, phone number, and company details when you submit a contact form, request a quote, or subscribe to our newsletter.</li>
          <li><strong>Technical Information</strong> — such as your IP address, browser type, device information, and operating system, collected automatically when you visit our site.</li>
          <li><strong>Usage Data</strong> — information about how you interact with our website, including pages visited, time spent, and referring URLs.</li>
          <li><strong>Cookies & Tracking Data</strong> — as described in our <Link href="/cookies-policy" className="text-secondary underline">Cookies Policy</Link>.</li>
        </ul>

        <h2 className="text-2xl font-bold text-secondary mt-10 mb-4">2. How We Use Your Information</h2>
        <p className="mb-4">We use the information we collect to:</p>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li>Respond to your inquiries and provide requested services</li>
          <li>Communicate with you about projects, proposals, and updates</li>
          <li>Improve our website, services, and user experience</li>
          <li>Send marketing communications, where you have consented to receive them</li>
          <li>Comply with legal obligations and protect our legal rights</li>
        </ul>

        <h2 className="text-2xl font-bold text-secondary mt-10 mb-4">3. How We Share Your Information</h2>
        <p className="mb-6">
          We do not sell your personal information. We may share your information with trusted
          third-party service providers who assist us in operating our website and delivering our
          services (such as hosting providers, analytics tools, or email platforms), and only to
          the extent necessary for them to perform their functions. We may also disclose
          information if required by law or to protect our rights, property, or safety.
        </p>

        <h2 className="text-2xl font-bold text-secondary mt-10 mb-4">4. Data Retention</h2>
        <p className="mb-6">
          We retain personal information only for as long as necessary to fulfill the purposes
          outlined in this policy, unless a longer retention period is required or permitted by law.
        </p>

        <h2 className="text-2xl font-bold text-secondary mt-10 mb-4">5. Data Security</h2>
        <p className="mb-6">
          We implement appropriate technical and organizational measures to protect your personal
          information against unauthorized access, alteration, disclosure, or destruction. However,
          no method of transmission over the internet is completely secure, and we cannot guarantee
          absolute security.
        </p>

        <h2 className="text-2xl font-bold text-secondary mt-10 mb-4">6. Your Rights</h2>
        <p className="mb-4">Depending on your location, you may have the right to:</p>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li>Access the personal information we hold about you</li>
          <li>Request correction or deletion of your personal information</li>
          <li>Object to or restrict certain processing of your data</li>
          <li>Withdraw consent at any time where processing is based on consent</li>
          <li>Request a copy of your data in a portable format</li>
        </ul>
        <p className="mb-6">
          To exercise any of these rights, please contact us using the details below.
        </p>

        <h2 className="text-2xl font-bold text-secondary mt-10 mb-4">7. Third-Party Links</h2>
        <p className="mb-6">
          Our website may contain links to third-party websites. We are not responsible for the
          privacy practices or content of those external sites and encourage you to review their
          privacy policies.
        </p>

        <h2 className="text-2xl font-bold text-secondary mt-10 mb-4">8. Children&apos;s Privacy</h2>
        <p className="mb-6">
          Our services are not directed to individuals under the age of 16, and we do not knowingly
          collect personal information from children.
        </p>

        <h2 className="text-2xl font-bold text-secondary mt-10 mb-4">9. Changes to This Policy</h2>
        <p className="mb-6">
          We may update this Privacy Policy from time to time to reflect changes in our practices or
          for legal, operational, or regulatory reasons. Any changes will be posted on this page
          with a revised &quot;Last updated&quot; date.
        </p>

        <h2 className="text-2xl font-bold text-secondary mt-10 mb-4">10. Contact Us</h2>
        <p>
          If you have any questions about this Privacy Policy or how we handle your data, please
          contact us through our{' '}
          <Link href="/contact-us" className="text-secondary underline">Contact page</Link>.
        </p>
      </section>

      <Footer />
    </>
  );
};

export default PrivacyPolicy;